import { CreateItemCost, ItemCost } from "@forgepoint/shared";

/** Identifies the BOM row an edit targets: by id once saved, by position before that. */
export interface BomRowTarget {
  id?: string;
  index: number;
  /** Rows present when the edit started; guards the fallback to position. */
  rowCount: number;
}

/**
 * Rebuilds a BOM array with the targeted row replaced.
 * @param rows - The operations or components to rebuild
 * @param target - The row to replace
 * @param replacement - The row to put in its place
 * @returns A new array in which only the targeted row changed
 */
export function replaceTargetRow<T extends { id?: string }>(
  rows: T[],
  target: BomRowTarget,
  replacement: T,
): T[] {
  const isTarget = buildTargetMatcher(rows, target);
  return rows.map((row, index) => (isTarget(row, index) ? replacement : row));
}

/**
 * Rebuilds a BOM array without the targeted row.
 * @param rows - The operations or components to rebuild
 * @param target - The row to remove
 * @returns A new array holding every row but the targeted one
 */
export function filterTargetRow<T extends { id?: string }>(
  rows: T[],
  target: BomRowTarget,
): T[] {
  const isTarget = buildTargetMatcher(rows, target);
  return rows.filter((row, index) => !isTarget(row, index));
}

/**
 * Matches the targeted row against the array as it stands now. A row added in
 * this session has no id yet, and saving components re-inserts every row under
 * a new id. Order and length survive both, so position stands in. An add or a
 * remove changes the length, and the edit then matches nothing rather than the
 * wrong row.
 */
function buildTargetMatcher<T extends { id?: string }>(
  rows: T[],
  target: BomRowTarget,
): (row: T, index: number) => boolean {
  const targetId = target.id;
  if (targetId && rows.some((row) => row.id === targetId)) {
    return (row) => row.id === targetId;
  }
  if (rows.length === target.rowCount) {
    return (_row, index) => index === target.index;
  }
  return () => false;
}

/**
 * Rewrites an item's cost breaks with a new base unit cost, keeping every
 * other break as it is.
 * @param costBreaks - The item's current cost breaks
 * @param unitCost - The unit cost to store on the base break
 * @returns The cost breaks to save, holding a base break in every case
 */
export function withBaseUnitCost(
  costBreaks: ItemCost[] | undefined,
  unitCost: string,
): CreateItemCost[] {
  if (!costBreaks || costBreaks.length === 0) {
    return [{ minQty: 1, unitCost }];
  }
  const baseIndex = baseCostBreakIndex(costBreaks);
  return costBreaks.map((costBreak, index) => ({
    minQty: costBreak.minQty,
    unitCost: index === baseIndex ? unitCost : costBreak.unitCost,
  }));
}

/**
 * Reads the unit cost the item cost field shows.
 * @param costBreaks - The item's current cost breaks
 * @returns The base break's unit cost, or an empty string when there is none
 */
export function baseUnitCost(costBreaks: ItemCost[] | undefined): string {
  if (!costBreaks || costBreaks.length === 0) {
    return "";
  }
  return costBreaks[baseCostBreakIndex(costBreaks)].unitCost;
}

/** The break an item cost edit reads and writes: the minQty 1 break, else the first. */
function baseCostBreakIndex(costBreaks: ItemCost[]): number {
  const index = costBreaks.findIndex((costBreak) => costBreak.minQty === 1);
  return index === -1 ? 0 : index;
}
